import React from 'react'
import EmailIcon from '@mui/icons-material/Email'
import FormatClearIcon from '@mui/icons-material/FormatClear'
import SaveIcon from '@mui/icons-material/Save'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Paper from '@mui/material/Paper'
import { styled } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import { useDispatch, useSelector } from 'react-redux'
import { useFirebase } from 'react-redux-firebase'
import { REGEX_EMAIL } from '../../../config'
import { onIsLoading } from '../../../utils/onLoading'
import { validatePassword } from '../../../utils/validatePassword'
import { actions as appActions } from '../../App/reducer'
import { getUserInfo } from '../../App/selectors'
import { ActionsDivComponent } from '../../Register/Actions'
import { actions as registerActions } from '../../Register/reducer'
import { actions, CreateLoginFormValues } from './reducer'
import { getLoginForm } from './selectors'
import type { CreateLoginForm } from './reducer'

const PaperComponent = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.grey[900],
}))

export const CreateLoginComponent = () => {
  const dispatch = useDispatch()
  const firebase = useFirebase()

  const [showPassword, setShowPassword] = React.useState(false)
  const [emailNotValid, setEmailNotValid] = React.useState(false)
  const [passwordNotValid, setPasswordNotValid] = React.useState(false)
  const [passwordConfirmNotValid, setPasswordConfirmNotValid] =
    React.useState(false)

  const loginForm = useSelector(getLoginForm)
  const userInfo = useSelector(getUserInfo)
  const loginFormRef = React.useRef(loginForm)
  const isMountedRef = React.useRef(true)
  loginFormRef.current = loginForm

  React.useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const { email, password, passwordConfirm } = loginForm

  const onShowPassword = React.useCallback(() => setShowPassword((show) => !show), [])

  const onMouseDownPassword = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
    },
    []
  )

  const onChange = React.useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      prop: keyof CreateLoginForm
    ) => {
      dispatch(
        actions.onLoginForm({
          ...loginFormRef.current,
          [prop]: e.target.value,
        })
      )
    },
    [dispatch]
  )

  const onReset = React.useCallback(() => {
    dispatch(actions.onLoginForm(CreateLoginFormValues))
    setEmailNotValid(false)
    setPasswordNotValid(false)
    setPasswordConfirmNotValid(false)
  }, [dispatch])

  const onSubmit = React.useCallback(() => {
    const form = loginFormRef.current
    onIsLoading(true)
    firebase
      .auth()
      .createUserWithEmailAndPassword(form.email, form.password)
      .catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.log('Error!', error.message)
        }
        if (isMountedRef.current) {
          dispatch(
            appActions.onAlert({
              severity: 'error',
              message: 'Failed to create login',
            })
          )
        }
      })
      .finally(() => {
        if (isMountedRef.current) {
          onIsLoading(false)
        }
      })
  }, [dispatch, firebase])

  React.useEffect(() => {
    onReset()
  }, [onReset])

  React.useEffect(() => {
    if (userInfo) {
      onReset()
      onIsLoading(false)
      dispatch(registerActions.onActiveStep(1))
    }
  }, [userInfo, dispatch, onReset])

  const disabled =
    emailNotValid ||
    passwordNotValid ||
    passwordConfirmNotValid ||
    !email ||
    !password ||
    !passwordConfirm

  return (
    <PaperComponent variant="outlined">
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={4}>
          <TextField
            label="Email"
            fullWidth
            value={email}
            onChange={(e) => onChange(e, 'email')}
            error={emailNotValid}
            helperText={emailNotValid ? 'Email not valid' : ''}
            onBlur={() => email && setEmailNotValid(!REGEX_EMAIL.test(email))}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <EmailIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <TextField
            label="Password"
            fullWidth
            value={password}
            onChange={(e) => onChange(e, 'password')}
            error={passwordNotValid}
            type={showPassword ? 'text' : 'password'}
            onBlur={() => {
              if (!password) return
              setPasswordNotValid(!validatePassword(password).isValid)
            }}
            helperText={
              <>
                Password Requirements: <br />
                minimum 8 characters (uppercase, lowercase) <br />
                minimum 1 numbers
                <br />
                minimum 1 special character
              </>
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={onShowPassword}
                    onMouseDown={onMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <TextField
            label="Confirm Password"
            fullWidth
            value={passwordConfirm}
            onChange={(e) => onChange(e, 'passwordConfirm')}
            error={passwordConfirmNotValid}
            onBlur={() =>
              passwordConfirm &&
              setPasswordConfirmNotValid(password !== passwordConfirm)
            }
            helperText={passwordConfirmNotValid ? 'Passwords do not match' : ''}
            type={showPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={onShowPassword}
                    onMouseDown={onMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
      <ActionsDivComponent
        onActionLeft={onReset}
        leftIcon={<FormatClearIcon />}
        textLeft="Reset"
        colorLeft="error"
        leftVariant="outlined"
        onActionRight={onSubmit}
        rightIcon={<SaveIcon />}
        textRight="Submit"
        colorRight="success"
        rightVariant="contained"
        disabledRight={disabled}
      />
    </PaperComponent>
  )
}
